/**
 * City - A city that the player must defend
 * Has alive/destroyed state with different visual representations.
 * Features ambient glow, window lights, and detailed building silhouettes.
 */

export class City {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.alive = true;
    this.width = 40;
    this.height = 30;
    this.glowIntensity = 1.0;
    this.glowPhase = Math.random() * Math.PI * 2; // Random phase for pulsing
    this.time = 0;

    // Generate random window pattern for this city (unique per city)
    this._windows = this._generateWindows();

    // Destruction animation
    this.destroyTimer = 0;
    this.destroying = false;
    this.destroyParticles = [];

    // Atomic explosion animation
    this.nukeActive = false;
    this.nukeTimer = 0;
    this.nukeFlashAlpha = 0;
    this.nukeShockwaveRadius = 0;
    this.nukeShockwaveAlpha = 0;
    this.nukeFireballRadius = 0;
    this.nukeStemHeight = 0;
    this.nukeCapRadius = 0;
    this.nukeCapY = 0;

    // Persistent smoke (rises while city is destroyed until wave ends)
    this.smokeParticles = [];
    this.smokeSpawnTimer = 0;
    this.smokePersistent = false;
  }

  destroy() {
    this.alive = false;
    this.glowIntensity = 0;
    this.destroying = true;
    this.destroyTimer = 0;

    // Initialize atomic explosion
    this.nukeActive = true;
    this.nukeTimer = 0;
    this.nukeFlashAlpha = 1.0;
    this.nukeShockwaveRadius = 0;
    this.nukeShockwaveAlpha = 1.0;
    this.nukeFireballRadius = 0;
    this.nukeStemHeight = 0;
    this.nukeCapRadius = 0;
    this.nukeCapY = this.y;

    // Start persistent smoke
    this.smokePersistent = true;
    this.smokeParticles = [];
    this.smokeSpawnTimer = 0;

    // Generate destruction debris particles
    this._spawnDestroyParticles();
  }

  restore() {
    this.alive = true;
    this.glowIntensity = 1.0;
    this.destroying = false;
    this.destroyTimer = 0;
    this.destroyParticles = [];
    this.nukeActive = false;
    this.smokePersistent = false;
    this.smokeParticles = [];
    this._windows = this._generateWindows();
  }

  /**
   * Stop persistent smoke generation (called at wave end).
   * Existing smoke particles will fade out naturally.
   */
  clearSmoke() {
    this.smokePersistent = false;
  }

  update(dt) {
    this.time += dt;

    if (this.alive) {
      // Pulsing ambient glow (warm oscillation between 0.65 and 1.0)
      this.glowIntensity = 0.65 + 0.35 * Math.sin(this.time * 1.5 + this.glowPhase);
    }

    // Update destruction particles
    if (this.destroying) {
      this.destroyTimer += dt;
      for (let i = this.destroyParticles.length - 1; i >= 0; i--) {
        const p = this.destroyParticles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 120 * dt; // gravity
        p.life -= dt;
        if (p.life <= 0) {
          this.destroyParticles.splice(i, 1);
        }
      }
      if (this.destroyTimer > 1.5) {
        this.destroying = false;
      }
    }

    // Atomic explosion animation (3s duration)
    if (this.nukeActive) {
      this._updateNukeExplosion(dt);
    }

    // Persistent smoke (continues while city is destroyed until wave ends)
    if (!this.alive && (this.smokePersistent || this.smokeParticles.length > 0)) {
      this._updatePersistentSmoke(dt);
    }
  }

  render(ctx) {
    // Render persistent smoke behind city (so rubble is on top of smoke base)
    if (this.smokeParticles.length > 0) {
      this._renderPersistentSmoke(ctx);
    }

    if (this.alive) {
      this._renderAlive(ctx);
    } else {
      this._renderDestroyed(ctx);
    }

    // Render atomic explosion effects (above rubble)
    if (this.nukeActive) {
      this._renderNukeExplosion(ctx);
    }

    // Render destruction particles (on top)
    if (this.destroying && this.destroyParticles.length > 0) {
      this._renderDestroyParticles(ctx);
    }
  }

  _renderAlive(ctx) {
    ctx.save();

    // Large ambient glow halo around the city (warm white/amber)
    const haloRadius = 50 * this.glowIntensity;
    const haloGlow = ctx.createRadialGradient(
      this.x, this.y - 10, 0,
      this.x, this.y - 10, haloRadius
    );
    haloGlow.addColorStop(0, `rgba(255, 221, 170, ${0.18 * this.glowIntensity})`);
    haloGlow.addColorStop(0.4, `rgba(255, 204, 102, ${0.10 * this.glowIntensity})`);
    haloGlow.addColorStop(0.7, `rgba(255, 170, 60, ${0.04 * this.glowIntensity})`);
    haloGlow.addColorStop(1, 'rgba(255, 170, 60, 0)');
    ctx.fillStyle = haloGlow;
    ctx.fillRect(this.x - haloRadius, this.y - 10 - haloRadius, haloRadius * 2, haloRadius * 2);

    // Ground-level ambient glow (radial gradient under city)
    const glowRadius = 40 * this.glowIntensity;
    const groundGlow = ctx.createRadialGradient(
      this.x, this.y + 2, 0,
      this.x, this.y + 2, glowRadius
    );
    groundGlow.addColorStop(0, `rgba(255, 204, 102, ${0.30 * this.glowIntensity})`);
    groundGlow.addColorStop(0.5, `rgba(255, 170, 60, ${0.15 * this.glowIntensity})`);
    groundGlow.addColorStop(1, 'rgba(255, 170, 60, 0)');
    ctx.fillStyle = groundGlow;
    ctx.fillRect(this.x - glowRadius, this.y - glowRadius, glowRadius * 2, glowRadius * 2);

    // Building shadows (shadow color + blur)
    ctx.shadowColor = '#ffcc66';
    ctx.shadowBlur = 15 * this.glowIntensity;

    // Building structures - varied heights for city skyline
    ctx.fillStyle = '#c4a46e';

    // Left building (shorter)
    ctx.fillRect(this.x - 16, this.y - 16, 9, 16);
    // Left building roof
    ctx.fillStyle = '#d4b47e';
    ctx.fillRect(this.x - 17, this.y - 17, 11, 2);

    // Center building (tallest)
    ctx.fillStyle = '#c4a46e';
    ctx.fillRect(this.x - 5, this.y - 26, 10, 26);
    // Center building roof (pointed)
    ctx.fillStyle = '#d4b47e';
    ctx.beginPath();
    ctx.moveTo(this.x - 6, this.y - 26);
    ctx.lineTo(this.x, this.y - 31);
    ctx.lineTo(this.x + 6, this.y - 26);
    ctx.closePath();
    ctx.fill();

    // Right building (medium)
    ctx.fillStyle = '#c4a46e';
    ctx.fillRect(this.x + 7, this.y - 20, 9, 20);
    // Right building roof
    ctx.fillStyle = '#d4b47e';
    ctx.fillRect(this.x + 6, this.y - 21, 11, 2);

    ctx.shadowBlur = 0;

    // Window lights (warm yellow/white glow with neon bloom)
    for (const win of this._windows) {
      // Flicker effect - some windows blink
      const flicker = win.flicker ? Math.sin(this.time * win.flickerSpeed + win.flickerPhase) > 0 : true;
      if (flicker) {
        ctx.fillStyle = `rgba(255, 238, 170, ${0.75 + 0.25 * this.glowIntensity})`;
        ctx.shadowColor = '#ffdd88';
        ctx.shadowBlur = 5 * this.glowIntensity;
        ctx.fillRect(this.x + win.ox, this.y + win.oy, win.w, win.h);
      }
    }

    ctx.restore();
  }

  _renderDestroyed(ctx) {
    ctx.save();

    // Smoke wisps (subtle)
    const smokeAlpha = Math.max(0, 0.15 - this.destroyTimer * 0.05);
    if (smokeAlpha > 0) {
      ctx.fillStyle = `rgba(80, 60, 40, ${smokeAlpha})`;
      const smokeOff = Math.sin(this.time * 2) * 3;
      ctx.beginPath();
      ctx.arc(this.x - 3 + smokeOff, this.y - 12, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(this.x + 5 + smokeOff * 0.7, this.y - 8, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Rubble pile - irregular shapes
    ctx.fillStyle = '#3a3530';
    ctx.beginPath();
    ctx.moveTo(this.x - 16, this.y);
    ctx.lineTo(this.x - 12, this.y - 6);
    ctx.lineTo(this.x - 6, this.y - 4);
    ctx.lineTo(this.x - 2, this.y - 8);
    ctx.lineTo(this.x + 4, this.y - 5);
    ctx.lineTo(this.x + 10, this.y - 7);
    ctx.lineTo(this.x + 14, this.y - 3);
    ctx.lineTo(this.x + 16, this.y);
    ctx.closePath();
    ctx.fill();

    // Darker rubble detail
    ctx.fillStyle = '#2a2520';
    ctx.fillRect(this.x - 8, this.y - 3, 5, 3);
    ctx.fillRect(this.x + 2, this.y - 4, 4, 4);
    ctx.fillRect(this.x + 8, this.y - 2, 3, 2);

    // Ember glow spots
    const emberAlpha = 0.3 + 0.15 * Math.sin(this.time * 3);
    ctx.fillStyle = `rgba(255, 100, 30, ${emberAlpha})`;
    ctx.fillRect(this.x - 4, this.y - 5, 2, 2);
    ctx.fillRect(this.x + 6, this.y - 4, 2, 2);

    ctx.restore();
  }

  /**
   * Update the atomic explosion animation phases.
   * Flash (0-0.3s), Shockwave (0-0.8s), Fireball (0-0.8s),
   * Mushroom stem (0.15-3s), Mushroom cap (0.4-3s).
   */
  _updateNukeExplosion(dt) {
    this.nukeTimer += dt;
    const t = this.nukeTimer;

    // Flash: bright white, fades over 0.3s
    if (t < 0.3) {
      this.nukeFlashAlpha = Math.pow(1.0 - (t / 0.3), 2);
    } else {
      this.nukeFlashAlpha = 0;
    }

    // Shockwave ring: expands over 0.8s
    if (t < 0.8) {
      const st = t / 0.8;
      this.nukeShockwaveRadius = st * 110;
      this.nukeShockwaveAlpha = (1 - st) * 0.8;
    } else {
      this.nukeShockwaveAlpha = 0;
    }

    // Fireball: expands then shrinks (0-0.8s)
    if (t < 0.8) {
      const ft = t / 0.8;
      this.nukeFireballRadius = Math.sin(ft * Math.PI) * 35;
    } else {
      this.nukeFireballRadius = Math.max(0, this.nukeFireballRadius - dt * 50);
    }

    // Mushroom stem: grows upward (0.15-2.5s)
    if (t > 0.15 && t < 2.5) {
      const stemT = Math.min(1, (t - 0.15) / 1.5);
      const eased = 1 - Math.pow(1 - stemT, 2); // ease out quadratic
      this.nukeStemHeight = eased * 85;
    }

    // Mushroom cap: expands at top of stem (0.4-3.0s)
    if (t > 0.4 && t < 3.0) {
      const capT = Math.min(1, (t - 0.4) / 1.6);
      const eased = 1 - Math.pow(1 - capT, 3); // ease out cubic
      this.nukeCapRadius = eased * 28;
      this.nukeCapY = this.y - this.nukeStemHeight;
    }

    // Nuke animation ends after 3.5s (persistent smoke continues separately)
    if (t > 3.5) {
      this.nukeActive = false;
    }
  }

  /**
   * Render the atomic explosion: flash, shockwave ring, fireball, mushroom stem, mushroom cap.
   */
  _renderNukeExplosion(ctx) {
    const t = this.nukeTimer;

    ctx.save();

    // 1. Flash - bright white/yellow radial burst
    if (this.nukeFlashAlpha > 0.01) {
      const flashRadius = 70 + (1 - this.nukeFlashAlpha) * 50;
      const flashGrad = ctx.createRadialGradient(
        this.x, this.y - 10, 0,
        this.x, this.y - 10, flashRadius
      );
      flashGrad.addColorStop(0, `rgba(255, 255, 255, ${this.nukeFlashAlpha})`);
      flashGrad.addColorStop(0.2, `rgba(255, 255, 200, ${this.nukeFlashAlpha * 0.9})`);
      flashGrad.addColorStop(0.5, `rgba(255, 200, 100, ${this.nukeFlashAlpha * 0.6})`);
      flashGrad.addColorStop(0.8, `rgba(255, 150, 50, ${this.nukeFlashAlpha * 0.3})`);
      flashGrad.addColorStop(1, 'rgba(255, 100, 30, 0)');
      ctx.fillStyle = flashGrad;
      ctx.beginPath();
      ctx.arc(this.x, this.y - 10, flashRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // 2. Shockwave ring - expanding circle with glow
    if (this.nukeShockwaveAlpha > 0.01 && this.nukeShockwaveRadius > 0) {
      const ringWidth = Math.max(1.5, (1 - this.nukeShockwaveRadius / 110) * 4 + 1);
      ctx.strokeStyle = `rgba(255, 220, 120, ${this.nukeShockwaveAlpha})`;
      ctx.shadowColor = '#ff8800';
      ctx.shadowBlur = 12 * this.nukeShockwaveAlpha;
      ctx.lineWidth = ringWidth;
      ctx.beginPath();
      ctx.arc(this.x, this.y - 10, this.nukeShockwaveRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // 3. Fireball at base of explosion
    if (this.nukeFireballRadius > 2) {
      const fbGrad = ctx.createRadialGradient(
        this.x, this.y - 8, 0,
        this.x, this.y - 8, this.nukeFireballRadius
      );
      fbGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      fbGrad.addColorStop(0.2, 'rgba(255, 230, 100, 0.85)');
      fbGrad.addColorStop(0.5, 'rgba(255, 140, 30, 0.6)');
      fbGrad.addColorStop(0.8, 'rgba(200, 60, 0, 0.3)');
      fbGrad.addColorStop(1, 'rgba(150, 30, 0, 0)');
      ctx.fillStyle = fbGrad;
      ctx.beginPath();
      ctx.arc(this.x, this.y - 8, this.nukeFireballRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // 4. Mushroom stem - tapered column rising from blast
    if (this.nukeStemHeight > 3) {
      const stemFade = Math.min(1, Math.max(0, (3.5 - t) / 1.2));
      if (stemFade > 0.01) {
        const stemBaseWidth = 10;
        const stemTopWidth = 6;
        const stemBot = this.y - 8;
        const stemTop = this.y - this.nukeStemHeight;

        // Stem gradient: hot at bottom, cooler at top
        const stemGrad = ctx.createLinearGradient(this.x, stemBot, this.x, stemTop);
        stemGrad.addColorStop(0, `rgba(255, 160, 60, ${0.75 * stemFade})`);
        stemGrad.addColorStop(0.3, `rgba(220, 120, 50, ${0.65 * stemFade})`);
        stemGrad.addColorStop(0.6, `rgba(170, 110, 80, ${0.55 * stemFade})`);
        stemGrad.addColorStop(1, `rgba(130, 100, 85, ${0.45 * stemFade})`);

        ctx.fillStyle = stemGrad;
        ctx.beginPath();
        ctx.moveTo(this.x - stemBaseWidth, stemBot);
        ctx.quadraticCurveTo(this.x - stemTopWidth * 0.7, (stemBot + stemTop) / 2, this.x - stemTopWidth, stemTop);
        ctx.lineTo(this.x + stemTopWidth, stemTop);
        ctx.quadraticCurveTo(this.x + stemTopWidth * 0.7, (stemBot + stemTop) / 2, this.x + stemBaseWidth, stemBot);
        ctx.closePath();
        ctx.fill();

        // Inner hot core in stem
        const coreGrad = ctx.createLinearGradient(this.x, stemBot, this.x, stemTop);
        coreGrad.addColorStop(0, `rgba(255, 230, 150, ${0.4 * stemFade})`);
        coreGrad.addColorStop(0.5, `rgba(255, 180, 100, ${0.2 * stemFade})`);
        coreGrad.addColorStop(1, 'rgba(200, 150, 100, 0)');
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.moveTo(this.x - stemBaseWidth * 0.4, stemBot);
        ctx.lineTo(this.x - stemTopWidth * 0.3, stemTop + (stemBot - stemTop) * 0.3);
        ctx.lineTo(this.x + stemTopWidth * 0.3, stemTop + (stemBot - stemTop) * 0.3);
        ctx.lineTo(this.x + stemBaseWidth * 0.4, stemBot);
        ctx.closePath();
        ctx.fill();
      }
    }

    // 5. Mushroom cap - billowing dome at top of stem
    if (this.nukeCapRadius > 3) {
      const capFade = Math.min(1, Math.max(0, (3.5 - t) / 1.2));
      if (capFade > 0.01) {
        const capY = this.nukeCapY;
        const rx = this.nukeCapRadius * 1.4; // wider than tall
        const ry = this.nukeCapRadius * 0.85;

        // Slight billowing wobble
        const wobble = Math.sin(t * 4) * 1.5;

        // Outer cap dome
        const capGrad = ctx.createRadialGradient(
          this.x, capY - 2, ry * 0.15,
          this.x, capY, Math.max(rx, ry)
        );
        capGrad.addColorStop(0, `rgba(255, 220, 130, ${0.8 * capFade})`);
        capGrad.addColorStop(0.3, `rgba(220, 150, 70, ${0.65 * capFade})`);
        capGrad.addColorStop(0.6, `rgba(170, 120, 80, ${0.5 * capFade})`);
        capGrad.addColorStop(0.85, `rgba(130, 105, 85, ${0.35 * capFade})`);
        capGrad.addColorStop(1, 'rgba(100, 85, 75, 0)');

        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.ellipse(this.x + wobble, capY, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright core of cap
        const innerGrad = ctx.createRadialGradient(
          this.x, capY - 2, 0,
          this.x, capY, ry * 0.55
        );
        innerGrad.addColorStop(0, `rgba(255, 255, 210, ${0.55 * capFade})`);
        innerGrad.addColorStop(0.5, `rgba(255, 200, 120, ${0.3 * capFade})`);
        innerGrad.addColorStop(1, 'rgba(255, 170, 80, 0)');
        ctx.fillStyle = innerGrad;
        ctx.beginPath();
        ctx.ellipse(this.x + wobble * 0.5, capY - 2, rx * 0.5, ry * 0.45, 0, 0, Math.PI * 2);
        ctx.fill();

        // Lower ring/torus effect where cap meets stem
        if (this.nukeStemHeight > 10) {
          const ringY = capY + ry * 0.6;
          const ringRx = rx * 0.7;
          const ringRy = ry * 0.25;
          ctx.fillStyle = `rgba(180, 130, 80, ${0.4 * capFade})`;
          ctx.beginPath();
          ctx.ellipse(this.x + wobble * 0.3, ringY, ringRx, ringRy, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.restore();
  }

  /**
   * Update persistent smoke particles that rise from destroyed city.
   */
  _updatePersistentSmoke(dt) {
    this.smokeSpawnTimer += dt;

    // Spawn new smoke particles while persistent flag is set
    if (this.smokePersistent && this.smokeSpawnTimer >= 0.12) {
      this.smokeSpawnTimer = 0;
      const spawnX = this.x + (Math.random() - 0.5) * 18;
      this.smokeParticles.push({
        x: spawnX,
        y: this.y - 8,
        vx: (Math.random() - 0.5) * 6,
        vy: -18 - Math.random() * 14,
        size: 2.5 + Math.random() * 4,
        life: 3.5 + Math.random() * 2,
        maxLife: 5.5,
        alpha: 0.35 + Math.random() * 0.15,
      });
    }

    // Update existing smoke particles
    for (let i = this.smokeParticles.length - 1; i >= 0; i--) {
      const s = this.smokeParticles[i];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vx += (Math.random() - 0.5) * 3 * dt; // horizontal drift
      s.vy *= 0.998; // slight deceleration
      s.size += dt * 1.8; // gradually expand
      s.life -= dt;
      s.alpha = Math.max(0, (s.life / s.maxLife) * 0.35);

      if (s.life <= 0) {
        this.smokeParticles.splice(i, 1);
      }
    }

    // Cap smoke particles for performance (max 35 per city)
    while (this.smokeParticles.length > 35) {
      this.smokeParticles.shift();
    }
  }

  /**
   * Render persistent smoke rising from destroyed city rubble.
   */
  _renderPersistentSmoke(ctx) {
    if (this.smokeParticles.length === 0) return;

    ctx.save();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    for (const s of this.smokeParticles) {
      if (s.alpha <= 0.01) continue;
      ctx.globalAlpha = s.alpha;
      // Gradient from warm brown/gray at bottom to cooler gray as smoke rises
      const warmth = Math.max(0, Math.min(1, (this.y - s.y) / 80));
      const r = Math.round(130 - warmth * 30);
      const g = Math.round(110 - warmth * 20);
      const b = Math.round(90 + warmth * 10);
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  _generateWindows() {
    const windows = [];
    // Left building windows (2 columns, 3 rows)
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 2; col++) {
        if (Math.random() > 0.25) { // 75% chance window is lit
          windows.push({
            ox: -15 + col * 5,
            oy: -14 + row * 5,
            w: 3,
            h: 3,
            flicker: Math.random() > 0.7,
            flickerSpeed: 2 + Math.random() * 4,
            flickerPhase: Math.random() * Math.PI * 2,
          });
        }
      }
    }
    // Center building windows (2 columns, 5 rows)
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 2; col++) {
        if (Math.random() > 0.2) { // 80% chance
          windows.push({
            ox: -4 + col * 5,
            oy: -24 + row * 5,
            w: 3,
            h: 3,
            flicker: Math.random() > 0.7,
            flickerSpeed: 2 + Math.random() * 4,
            flickerPhase: Math.random() * Math.PI * 2,
          });
        }
      }
    }
    // Right building windows (2 columns, 4 rows)
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 2; col++) {
        if (Math.random() > 0.3) { // 70% chance
          windows.push({
            ox: 8 + col * 5,
            oy: -18 + row * 5,
            w: 3,
            h: 3,
            flicker: Math.random() > 0.7,
            flickerSpeed: 2 + Math.random() * 4,
            flickerPhase: Math.random() * Math.PI * 2,
          });
        }
      }
    }
    return windows;
  }

  _spawnDestroyParticles() {
    this.destroyParticles = [];
    for (let i = 0; i < 12; i++) {
      this.destroyParticles.push({
        x: this.x + (Math.random() - 0.5) * 20,
        y: this.y - Math.random() * 20,
        vx: (Math.random() - 0.5) * 80,
        vy: -Math.random() * 100 - 30,
        life: 0.5 + Math.random() * 0.8,
        size: 1 + Math.random() * 3,
        color: Math.random() > 0.5 ? '#ff6600' : '#ffaa33',
      });
    }
  }

  _renderDestroyParticles(ctx) {
    ctx.save();
    for (const p of this.destroyParticles) {
      const alpha = Math.max(0, p.life / 1.0);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.restore();
  }
}
